import { c as _c } from "react/compiler-runtime";
import type { Address } from '@morpho-org/blue-sdk';
import { type InterfaceInputToken, parseApiAsset } from '@morpho-org/dapp-core';
import { formatLongString } from '@morpho-org/morpho-ts';
import { useDialogContext } from '@morpho-org/ui-v6/contexts';
import { formatters, getDigitsFromPriceAndAmount } from '@morpho-org/ui-v6/utils';
import { useCallback, useMemo } from 'react';
import { useChainNavigation } from '@/hooks/useChainNavigation';
import { useFeatureFlags } from '@/lib/shared/components/FeatureFlagsProvider';
import { POSITION_MANAGER_DIALOG_OPTIONS } from '../../PositionManager/dialogOptions';
import { PositionManagerDepositDialog } from '../../PositionManager/PositionManagerDepositDialog';
import { PositionManagerWithdrawDialog } from '../../PositionManager/PositionManagerWithdrawDialog';
import { VaultV2PositionManagerDepositDialog } from '../../PositionManager/VaultV2PositionManager/VaultV2PositionManagerDepositDialog';
import { VaultV2PositionManagerWithdrawDialog } from '../../PositionManager/VaultV2PositionManager/VaultV2PositionManagerWithdrawDialog';
interface UseMobileEarnCardInput {
  vaultAddress: Address;
  asset: InterfaceInputToken;
  amount: bigint | null | undefined;
  usdValue: bigint | null | undefined;
  liquidity?: {
    amount: bigint | null | undefined;
    usdValue: bigint | null | undefined;
  };
  isVaultV2?: boolean;
  /** False when browsing another address's portfolio — that position isn't actionable. */
  isSelf: boolean;
}
export const useMobileEarnCard = t0 => {
  const $ = _c(42);
  const {
    vaultAddress,
    asset,
    amount,
    usdValue,
    liquidity,
    isVaultV2,
    isSelf
  } = t0;
  const {
    positionManagerDepositIntoVault,
    positionManagerWithdrawVaultDeposit
  } = useFeatureFlags();
  const {
    toggleDialog
  } = useDialogContext();
  let t1;
  if ($[0] !== asset.chain.id) {
    t1 = {
      chainId: asset.chain.id
    };
    $[0] = asset.chain.id;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    syncChainSwitch
  } = useChainNavigation(t1);
  let t2;
  if ($[2] !== asset) {
    t2 = parseApiAsset(asset);
    $[2] = asset;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const vaultAsset = t2;
  let t3;
  bb0: {
    if (!usdValue) {
      t3 = undefined;
      break bb0;
    }
    let t4;
    if ($[4] !== usdValue) {
      t4 = formatters.short.unit("$").digits(2).of(usdValue, 18);
      $[4] = usdValue;
      $[5] = t4;
    } else {
      t4 = $[5];
    }
    t3 = t4;
  }
  const formattedUsdValue = t3;
  let t4;
  if ($[6] !== amount || $[7] !== vaultAsset.decimals || $[8] !== vaultAsset.price || $[9] !== vaultAsset.symbol) {
    t4 = formatters.short.digits(getDigitsFromPriceAndAmount(vaultAsset.price, amount, vaultAsset.decimals)).unit(formatLongString(vaultAsset.symbol ?? "", 16)).of(amount, vaultAsset.decimals);
    $[6] = amount;
    $[7] = vaultAsset.decimals;
    $[8] = vaultAsset.price;
    $[9] = vaultAsset.symbol;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const formattedAmount = t4;
  let t5;
  if ($[11] !== liquidity?.usdValue) {
    t5 = () => formatters.short.unit("$").digits(2).of(liquidity?.usdValue, 18);
    $[11] = liquidity?.usdValue;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  liquidity?.usdValue;
  let t6;
  if ($[13] !== t5) {
    t6 = t5();
    $[13] = t5;
    $[14] = t6;
  } else {
    t6 = $[14];
  }
  const formattedLiquidityUsdValue = t6;
  let t7;
  bb1: {
    if (!liquidity) {
      t7 = undefined;
      break bb1;
    }
    let t8;
    if ($[15] !== liquidity.amount || $[16] !== vaultAsset.decimals || $[17] !== vaultAsset.price || $[18] !== vaultAsset.symbol) {
      t8 = formatters.short.digits(getDigitsFromPriceAndAmount(vaultAsset.price, liquidity.amount, vaultAsset.decimals)).unit(formatLongString(vaultAsset.symbol ?? "", 16)).of(liquidity.amount, vaultAsset.decimals);
      $[15] = liquidity.amount;
      $[16] = vaultAsset.decimals;
      $[17] = vaultAsset.price;
      $[18] = vaultAsset.symbol;
      $[19] = t8;
    } else {
      t8 = $[19];
    }
    t7 = t8;
  }
  const formattedLiquidityAmount = t7;
  let t8;
  if ($[20] !== asset.chain.id || $[21] !== isVaultV2 || $[22] !== syncChainSwitch || $[23] !== toggleDialog || $[24] !== vaultAddress || $[25] !== vaultAsset.address) {
    t8 = () => {
      syncChainSwitch();
      toggleDialog(isVaultV2 ? <VaultV2PositionManagerDepositDialog vaultAddress={vaultAddress} chainId={asset.chain.id} assetAddress={vaultAsset.address} /> : <PositionManagerDepositDialog vaultAddress={vaultAddress} chainId={asset.chain.id} assetAddress={vaultAsset.address} />, {
        ...POSITION_MANAGER_DIALOG_OPTIONS,
        fullSize: true
      });
    };
    $[20] = asset.chain.id;
    $[21] = isVaultV2;
    $[22] = syncChainSwitch;
    $[23] = toggleDialog;
    $[24] = vaultAddress;
    $[25] = vaultAsset.address;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const onDepositClick = t8;
  let t9;
  if ($[27] !== asset.chain.id || $[28] !== isVaultV2 || $[29] !== syncChainSwitch || $[30] !== toggleDialog || $[31] !== vaultAddress) {
    t9 = () => {
      syncChainSwitch();
      toggleDialog(isVaultV2 ? <VaultV2PositionManagerWithdrawDialog vaultAddress={vaultAddress} chainId={asset.chain.id} /> : <PositionManagerWithdrawDialog vaultAddress={vaultAddress} chainId={asset.chain.id} />, {
        ...POSITION_MANAGER_DIALOG_OPTIONS,
        fullSize: true
      });
    };
    $[27] = asset.chain.id;
    $[28] = isVaultV2;
    $[29] = syncChainSwitch;
    $[30] = toggleDialog;
    $[31] = vaultAddress;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const onWithdrawClick = t9;
  const t10 = positionManagerDepositIntoVault && isSelf;
  const t11 = positionManagerWithdrawVaultDeposit && isSelf;
  let t12;
  if ($[33] !== formattedAmount || $[34] !== formattedLiquidityAmount || $[35] !== formattedLiquidityUsdValue || $[36] !== formattedUsdValue || $[37] !== onDepositClick || $[38] !== onWithdrawClick || $[39] !== t10 || $[40] !== t11) {
    t12 = {
      formattedUsdValue,
      formattedAmount,
      formattedLiquidityUsdValue,
      formattedLiquidityAmount,
      onDepositClick,
      onWithdrawClick,
      canDeposit: t10,
      canWithdraw: t11
    };
    $[33] = formattedAmount;
    $[34] = formattedLiquidityAmount;
    $[35] = formattedLiquidityUsdValue;
    $[36] = formattedUsdValue;
    $[37] = onDepositClick;
    $[38] = onWithdrawClick;
    $[39] = t10;
    $[40] = t11;
    $[41] = t12;
  } else {
    t12 = $[41];
  }
  return t12;
};